import {defineStore} from 'pinia';
import {ResourceService} from 'src/services/ResourceService';
import {Services} from 'src/services/service-manager';
import {ref} from 'vue';
import {useGrouperStore} from 'stores/grouper-store';
import {AnalyzeDrgRequestDto} from 'src/contracts/analyzeDrgRequestDto';
import {GetResearchResponse, MetadataResearchSection} from 'src/contracts/getMetadataResearchResponse';

export const useResourcesStore = defineStore('resourcesStore', () => {

    const analysisData = ref<any>();
    const researchMetadata = ref<MetadataResearchSection[]>([]);


    async function getResearches() {
        const resourceService = Services.service(ResourceService);
        const diagnosisCodes = useGrouperStore().diagnosisCodes;

        const codes = diagnosisCodes.map(diagnose => diagnose.code).map(code => code.replace('\.', ''))
        const promises = codes.map(async code => {
            let result;
            try {
                result = await resourceService.researchData(code, 'ICD10CM_DX');
            } catch (e) {
                result = await resourceService.researchData(code, 'ICD10CM_E');
            }
            return result;
        });

        const responses = await Promise.all(promises);
        researchMetadata.value = aggregateResponses(responses);
    }

    function aggregateResponses(responses: (GetResearchResponse | undefined)[]): MetadataResearchSection[] {
        const aggregatedData: any = {
            code: responses[0]?.code,
            code_type: responses[0]?.code_type,
            research_sections: []
        };

        function findResearchSection(researchSections: MetadataResearchSection[], type: string): MetadataResearchSection | undefined {
            return researchSections.find(section => section.research_type === type);
        }

        responses.forEach(response => {
            response?.research_sections.forEach(section => {
                const existingSection = findResearchSection(aggregatedData.research_sections, section.research_type);

                if (existingSection) {
                    existingSection.articles.push(...section?.articles??[]);
                    existingSection.best_count += section.best_count??0;
                    existingSection.other_count += section.other_count??0;
                } else {
                    aggregatedData.research_sections.push({
                        ...section,
                        best_count: section.best_count??0,
                        other_count: section.other_count??0,
                        articles: [...section?.articles??[]]
                    });
                }
            });
        });

        return aggregatedData.research_sections;
    }

    async function getMsDrgAnalysis(): Promise<void> {
        const info = useGrouperStore().primaryDRG;
        const patientData = useGrouperStore().patient;

        const diagnosis = useGrouperStore().diagnosisCodes;
        const cc_present = diagnosis.some(diagnose => diagnose.drgModifier?.includes('CC'));

        const request: AnalyzeDrgRequestDto = {
            drg_analysis_input: {
                drg: info?.drg,
                grouper_version: grouperVersion(info?.grouper_version),
                coding_system: patientData?.codingSystem,
                diagnoses:{
                    diagnosis: diagnosis
                },
                procedures:{
                    procedure: useGrouperStore().procedureCodes
                },
                los: patientData?.los,
                cc_is_present: cc_present
            }
        }

        const resourceService = Services.service(ResourceService);
        analysisData.value = await resourceService.analyzeDRG(request);
    }

    function grouperVersion(version: string | undefined | null) : string {
        return version?.slice(0, -1) + '.' + version?.slice(-1);
    }

    return {
        getResearches,
        getMsDrgAnalysis,
        analysisData,
        aggregateResponses,
        researchMetadata
    }
})
